// A small helper for mapping coordinates in a basic 200×200 coordinate plane
// with origin at the center. We'll use it for reflection preview.


export default function ShapePreview({ shape }) {
  const size = 250; // We'll do 200×200 for the entire SVG
  
  if (!shape || !shape.type) {
    return <div>No shape specified.</div>;
  }

  let svgElements = null;

  // We'll do a switch on shape.type
  switch (shape.type) {
    case "prismPreview": {
        const { l, w, h } = shape;
      
        // Overall 300×300 canvas
        const size = 300;
        // A decent scale for a noticeably large shape
        const scale = 20;
      
        // 1) Compute the actual pixel widths from L, W, H
        const frontW = l * scale;   // length in px
        const frontH = h * scale;   // height in px
        const sideOffset = w * scale * 0.6; // approximate isometric offset for width
      
        // 2) Figure out the bounding box of the shape:
        //    horizontally: from frontX -> frontX + frontW + sideOffset
        //    vertically:   from frontY - frontH - 0.5*sideOffset (top) -> frontY (bottom)
      
        // We'll position the shape so it's roughly centered in the 300×300 by calculating
        // how big it is horizontally & vertically, then shifting accordingly.
      
        // total “width” in x is frontW + sideOffset
        const shapeWidth = frontW + sideOffset;
        // total “height” in y is frontH + (0.5*sideOffset?), but we’ll just approximate with frontH + sideOffset
        const shapeHeight = frontH + sideOffset * 0.5;
      
        // Now we want to place the shape so its bounding box is near the center
        // We'll pick frontX so that (frontX + shapeWidth/2) ≈ size/2 => frontX = size/2 - shapeWidth/2
        // But we also want the shape’s “bottom” to be somewhat near the bottom of the canvas.
        // We'll do something like: frontX = size/2 - shapeWidth*0.5, frontY = size/2 + shapeHeight*0.4, etc.
        // Tweak to your liking:
      
        const frontX = size / 2 - shapeWidth * 0.5;
        const frontY = size / 2 + shapeHeight * 0.3;
      
        // 3) Define polygons for front, top, right faces
        const frontRect = [
          [frontX, frontY],
          [frontX + frontW, frontY],
          [frontX + frontW, frontY - frontH],
          [frontX, frontY - frontH]
        ];
        const topPoly = [
          [frontX, frontY - frontH],
          [frontX + sideOffset, frontY - frontH - 0.5 * sideOffset],
          [frontX + frontW + sideOffset, frontY - frontH - 0.5 * sideOffset],
          [frontX + frontW, frontY - frontH]
        ];
        const rightPoly = [
          [frontX + frontW, frontY],
          [frontX + frontW + sideOffset, frontY - 0.5 * sideOffset],
          [frontX + frontW + sideOffset, frontY - frontH - 0.5 * sideOffset],
          [frontX + frontW, frontY - frontH]
        ];
      
        // 4) Dimension lines + labels:
        //   For clarity, let’s show:
        //    - L in red across the “bottom front” edge,
        //    - W in blue from the front-right corner “back,”
        //    - H in green on the front-left vertical.
      
        // L line: from (frontX, frontY) → (frontX+frontW, frontY)
        const L_midX = frontX + frontW / 2;
        const L_midY = frontY + 18;
      
        // W line: from (frontX + frontW, frontY) → (frontX + frontW + sideOffset, frontY - 0.5*sideOffset)
        const Wx1 = frontX + frontW;
        const Wy1 = frontY;
        const Wx2 = Wx1 + sideOffset;
        const Wy2 = Wy1 - 0.5 * sideOffset;
        const W_midX = Wx1 + (Wx2 - Wx1) / 2 + 24;
        const W_midY = Wy1 + (Wy2 - Wy1) / 2 + 14;
      
        // H line: from (frontX, frontY) → (frontX, frontY - frontH)
        // so it’s on the “left” vertical, not overlapping the W line
        const H_midX = frontX - 10; // shift label a bit to the left
        const H_midY = frontY - frontH / 2.5;
      
        const svgElements = (
          <>
            {/* front face */}
            <polygon
              points={frontRect.map(pt => pt.join(",")).join(" ")}
              fill="#fff9c4"
              stroke="#fbc02d"
              strokeWidth={2}
            />
            {/* top face */}
            <polygon
              points={topPoly.map(pt => pt.join(",")).join(" ")}
              fill="#c5e1a5"
              stroke="#558b2f"
              strokeWidth={2}
            />
            {/* right face */}
            <polygon
              points={rightPoly.map(pt => pt.join(",")).join(" ")}
              fill="#ffe0b2"
              stroke="#f57c00"
              strokeWidth={2}
            />
      
            {/* L line + label (bottom front) */}
            <line
              x1={frontX}
              y1={frontY}
              x2={frontX + frontW}
              y2={frontY}
              stroke="red"
              strokeWidth={2}
            />
            <text
              x={L_midX}
              y={L_midY}
              fill="red"
              fontSize={18}
              fontWeight="bold"
              textAnchor="middle"
            >
              L={l}
            </text>
      
            {/* W line + label (bottom side) */}
            <line
              x1={Wx1}
              y1={Wy1}
              x2={Wx2}
              y2={Wy2}
              stroke="blue"
              strokeWidth={2}
            />
            <text
              x={W_midX}
              y={W_midY}
              fill="blue"
              fontSize={18}
              fontWeight="bold"
              textAnchor="middle"
            >
              W={w}
            </text>
      
            {/* H line + label (left vertical) */}
            <line
              x1={frontX}
              y1={frontY}
              x2={frontX}
              y2={frontY - frontH}
              stroke="green"
              strokeWidth={2}
            />
            <text
              x={H_midX}
              y={H_midY}
              fill="green"
              fontSize={18}
              fontWeight="bold"
              textAnchor="end"
            >
              H={h}
            </text>
          </>
        );
      
        return (
          <svg
            width={size}
            height={size}
            style={{ background: "#f9f9f9", border: "1px solid #ccc" }}
          >
            {svgElements}
          </svg>
        );
      }
      

/* ---------- triangleAngle ---------- */
case "triangleAngle": {
    /* ---------- unpack & defaults ---------- */
    const { a, b, c: cIn, hideC } = shape;
    const c = cIn !== undefined ? cIn : Math.max(1, 180 - a - b);
  
    /* ---------- adjustable GLOBAL scale  (0.5 … 1) ---------- */
    //   1.00  = “fill view‑box as much as possible”
    //   0.85  = 85 % of the maximum   (default below)
    // Change this single number if you just want the drawing smaller / larger.
    const triScale = 0.30;
  
    /* ---------- Step 1.  Build triangle with a simple base ---------- */
    const base = 110;                        // px (before global scale)
    let A = { x: 70, y: 200 };               // bottom‑left
    let B = { x: A.x + base, y: A.y };       // bottom‑right
  
    // AC from Law of Sines
    const toRad = deg => (deg * Math.PI) / 180;
    const AC = (base * Math.sin(toRad(b))) / Math.sin(toRad(c));
  
    let C = {
      x: A.x + AC * Math.cos(toRad(a)),
      y: A.y - AC * Math.sin(toRad(a))       // minus because SVG y axis ↘
    };
  
    /* ---------- Step 2.  Apply the *global* scale ---------- */
    if (triScale !== 1) {
      const g = v => ({ x: v.x * triScale, y: v.y * triScale });
      A = g(A);
      B = g(B);
      C = g(C);
    }
  
    /* ---------- Step 3.  Auto‑centre inside the 250×250 box ---------- */
    const view = { w: 250, h: 250 };
    const pad  = 5;                          // margin for labels
  
    const xs = [A.x, B.x, C.x];
    const ys = [A.y, B.y, C.y];
    const bb = {
      w: Math.max(...xs) - Math.min(...xs),
      h: Math.max(...ys) - Math.min(...ys),
      minX: Math.min(...xs),
      minY: Math.min(...ys)
    };
  
    const extraX = Math.max(0, (view.w - bb.w) / 2 - bb.minX + pad);
    const extraY = Math.max(0, (view.h - bb.h) / 2 - bb.minY + pad);
  
    const t = v => ({ x: v.x + extraX, y: v.y + extraY });
    A = t(A);  B = t(B);  C = t(C);
  
    /* ---------- SVG drawing ---------- */
    svgElements = (
      <>
        <polygon
          points={`${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y}`}
          fill="#ffebee"
          stroke="#b71c1c"
          strokeWidth="2"
        />
  
        <text x={A.x - 8}  y={A.y + 14} fill="red"   fontSize="13">A={a}°</text>
        <text x={B.x + 4}  y={B.y + 14} fill="blue"  fontSize="13">B={b}°</text>
  
        {!hideC && cIn !== undefined && (
          <text x={C.x - 18} y={C.y - 6} fill="green" fontSize="13">C={c}°</text>
        )}
        {hideC && (
          <text x={C.x - 12} y={C.y - 6} fill="green" fontSize="13">C=?</text>
        )}
      </>
    );
    break;
  }
  
  
      

  case "reflection": {
    const { original } = shape;  // e.g. {x:-1, y:-5}, axis:"y"
    const half   = size / 2;           // convenience
    const scale  = 15;                 // px per grid unit (tidy spacing)
  
    /* ---------- helpers ---------- */
    const toSvg = (x, y) => ({
      x: half + x * scale,   // right → +, left → –
      y: half - y * scale    // up → –, down → +
    });
  
    /* ---------- axes ---------- */
    const axes = (
      <>
        {/* x‑axis */}
        <line x1={0}   y1={half} x2={size} y2={half} stroke="#999" />
        {/* y‑axis */}
        <line x1={half} y1={0}   x2={half} y2={size} stroke="#999" />
        {/* axis labels */}
        <text x={half + 4} y={12}          fontSize="10">y</text>
        <text x={size - 12} y={half - 4}   fontSize="10">x</text>
      </>
    );
  
    /* ---------- original point ---------- */
    const P  = toSvg(original.x, original.y);
    const labelDx = original.x >= 0 ? 5 : -38;   // ~38 px → label width
  
    const point = (
      <>
        <circle cx={P.x} cy={P.y} r={4} fill="red" />
        <text x={P.x + labelDx} y={P.y - 5} fill="red" fontSize="12">
          ({original.x},{original.y})
        </text>
      </>
    );
  
    /* ---------- optional: reflected point ---------- */
    /*
    if (axis === "y") {
      const refl = { x: -original.x, y: original.y };
      const Rp   = toSvg(refl.x, refl.y);
      lines.push(
        <circle key="ref" cx={Rp.x} cy={Rp.y} r={4} fill="blue" />,
        <text   key="refLabel" x={Rp.x + (refl.x >= 0 ? 5 : -38)}
               y={Rp.y - 5} fill="blue" fontSize="12">
          ({refl.x},{refl.y})
        </text>
      );
    }
    */
  
    svgElements = (
      <>
        {axes}
        {point}
      </>
    );
    break;
  }
  
    case "circle": {
        const { radius } = shape; // shape has radius
        const size = 200;
        
        // e.g. scale = 35 -> bigger circle
        const scale = 35;
      
        // center the circle in 200×200
        const cx = size / 2;  // 100
        const cy = size / 2;  // 100
      
        // radius in pixels
        const rPx = radius * scale;
      
        // We'll draw the circle, the radius line, and two labels:
        // 1) "r={radius}" in center
        // 2) "radius" (or "r") near the radius line
      
        // Midpoint for line label
        const midX = cx + rPx / 2; 
        const midY = cy - 5;       // shift up a bit
      
        svgElements = (
          <>
            {/* The circle itself */}
            <circle
              cx={cx}
              cy={cy}
              r={rPx}
              fill="#ffe0b2"
              stroke="#f57c00"
              strokeWidth={2}
            />
      
            {/* The "r=2" text near the center of the circle */}
            <text
              x={cx}
              y={cy + 5} // a small offset so text is visually centered
              fill="black"
              fontSize={16}
              fontWeight="bold"
              textAnchor="middle"
            >
            </text>
      
            {/* The radius line from center to circle boundary (to the right) */}
            <line
              x1={cx}
              y1={cy}
              x2={cx + rPx}
              y2={cy}
              stroke="blue"
              strokeWidth={2}
            />
      
            {/* A label on that radius line */}
            <text
              x={midX}
              y={midY}
              fill="blue"
              fontSize={14}
              fontWeight="bold"
              textAnchor="middle"
            >
              radius={radius}
            </text>
          </>
        );
      
        return (
          <svg width={size} height={size} style={{ background: "#f9f9f9", border: "1px solid #ccc" }}>
            {svgElements}
          </svg>
        );
      }

      
    default:
      return <div>Unsupported shape type: {shape.type}</div>;
  }

  return (
    <svg width={size} height={size} style={{ background: "#f9f9f9", border: "1px solid #ccc" }}>
      {svgElements}
    </svg>
  );
}
